import { useEffect, useRef, useState } from "react";
import Image from "next/image";

import { motion, useScroll, useTransform } from "framer-motion";
import {
  BarChart,
  Camera,
  Clock,
  Link as LinkIcon,
  Phone,
  PlusCircle,
  RefreshCcw,
  Smartphone,
  Timer,
} from "lucide-react";

import VideoModalWithThumbnail from "@/components/seo/VideoModalWithThumbnail";
import { cn } from "@/lib/utils";
import CalendarBookingLink from "./CalendarBookingLink";
import SectionTag from "./SectionTag";

const whatYouGetSteps = [
  {
    media: (
      <div className="relative aspect-square h-fit w-full">
        <Image
          src="/how-it-works-1.png"
          alt="how-it-works-1"
          priority={false}
          sizes="(max-width: 768px) 90vw, 400px"
          fill
          className="rounded-lg object-contain"
        />
      </div>
    ),
    content: (
      <ContentContainer>
        <NumberBadge number={1} tilt="right" className="mb-8" />
        <ContentTitle text="A Free Website Up Front" className="mb-4" />
        <ContentDescription
          className="mb-6"
          text="Really, we build it for you up front on your schedule. No strings attached."
        />
        <CalendarBookingLink className="transition-transform hover:scale-105" />
      </ContentContainer>
    ),
  },
  {
    media: (
      <div className="relative aspect-square h-fit w-full">
        <Image
          src="/how-it-works-4.png"
          alt="how it works 4"
          priority={false}
          sizes="(max-width: 768px) 90vw, 400px"
          fill
          className="rounded-lg object-contain"
        />
      </div>
    ),
    content: (
      <ContentContainer>
        <NumberBadge number={2} tilt="right" className="mb-8" />
        <ContentTitle text="More Customers" className="mb-4" />

        <div className="overflow-hidden">
          <div className="py-6 pl-0 md:pl-6 md:pr-6 space-y-6">
            <div className="space-y-4">
              <p className="text-ash-600 leading-relaxed">
                Just as important as attracting leads is converting them into customers.
              </p>
              <div className="bg-rebolt-100 p-4 rounded-xl border border-rebolt-100">
                <p className="text-rebolt-700 font-medium">
                  We&apos;ll text message every lead directly to your phone as well as your email so you can follow up
                  quickly.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center gap-3 p-3 rounded-lg bg-ash-100">
                <div className="w-10 h-10 rounded-full bg-rebolt-100 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-rebolt-600" />
                </div>
                <span className="text-sm font-medium text-ash-700">Instant notifications</span>
              </div>

              <div className="flex items-center gap-3 p-3 rounded-lg bg-ash-100">
                <div className="w-10 h-10 rounded-full bg-rebolt-100 flex items-center justify-center">
                  <Smartphone className="w-5 h-5 text-rebolt-600" />
                </div>
                <span className="text-sm font-medium text-ash-700">SMS & Email alerts</span>
              </div>

              <div className="flex items-center gap-3 p-3 rounded-lg bg-ash-100">
                <div className="w-10 h-10 rounded-full bg-rebolt-100 flex items-center justify-center">
                  <Timer className="w-5 h-5 text-rebolt-600" />
                </div>
                <span className="text-sm font-medium text-ash-700">24/7 Coverage</span>
              </div>

              <div className="flex items-center gap-3 p-3 rounded-lg bg-ash-100">
                <div className="w-10 h-10 rounded-full bg-rebolt-100 flex items-center justify-center">
                  <BarChart className="w-5 h-5 text-rebolt-600" />
                </div>
                <span className="text-sm font-medium text-ash-700">Lead Analytics</span>
              </div>
            </div>
          </div>
        </div>
      </ContentContainer>
    ),
  },
  {
    media: (
      <VideoModalWithThumbnail
        className="w-10/12 mx-auto max-h-[600px] rounded-[20px] overflow-hidden group cursor-pointer"
        videoUrl="/videos/social-automation.mp4"
        thumbnailUrl="https://ik.imagekit.io/4wu305uo4/testimonials/thumbnails/social-automation-thumbnail.png?updatedAt=1734115173957"
      />
    ),
    content: (
      <ContentContainer>
        <NumberBadge number={3} tilt="left" className="mb-8" />
        <ContentTitle text="Plus These Extra Benefits" className="mb-6" />
        <ul className="space-y-4 w-full p-0 m-0">
          {[
            {
              title: "Social Media Posts Made Easy",
              description: "Send us your project photos - we handle everything else",
              icon: <Camera className="w-5 h-5 text-rebolt-600" />,
            },
            {
              title: "24/7 Direct Support",
              description: "Text or call anytime, get help within 24 hours",
              icon: <Phone className="w-5 h-5 text-rebolt-600" />,
            },
            {
              title: "Unlimited Changes",
              description: "Whatever you need changed or added, just ask - it's included",
              icon: <RefreshCcw className="w-5 h-5 text-rebolt-600" />,
            },
            {
              title: "Link-in-Bio Page",
              description: "A simple page with all your contact info, ready to share",
              icon: <LinkIcon className="w-5 h-5 text-rebolt-600" />,
            },
            {
              title: "And Much More!",
              description: "Discover all the extras that come with your package",
              icon: <PlusCircle className="w-5 h-5 text-rebolt-600" />,
            },
          ].map((item) => (
            <li
              key={item.title}
              className="flex items-start gap-4 p-4 rounded-xl transition-all duration-300 hover:bg-ash-100 border border-transparent hover:border-ash-200 group cursor-default"
            >
              <div className="shrink-0">
                <div className="w-10 h-10 rounded-full bg-rebolt-100 flex items-center justify-center group-hover:bg-rebolt-100 transition-colors duration-300">
                  {item.icon}
                </div>
              </div>
              <div className="flex flex-col">
                <ContentTitle text={item.title} className="font-semibold text-ash-900 text-base md:text-left" />
                <ContentDescription text={item.description} className="text-ash-600 text-sm text-left" />
              </div>
            </li>
          ))}
        </ul>
      </ContentContainer>
    ),
  },
];
const WhatYouGetSection = ({ city, industry }: { city?: string; industry: string }) => {
  const ref = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);

  useEffect(() => {
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect();
      setHeight(rect.height);
    }
  }, [ref]);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start 25%", "end 40%"],
  });

  const heightTransform = useTransform(scrollYProgress, [0, 1], [0, height]);
  const opacityTransform = useTransform(scrollYProgress, [0, 0.1], [0, 1]);

  return (
    <div className="w-full bg-white md:px-10 py-20" ref={containerRef}>
      <div className="text-center max-w-7xl mx-auto mb-10 md:mb-0">
        <SectionTag
          text={city ? `${city} ${industry} Web Design Services` : `${industry} Web Design Services`}
          className="mx-auto"
        />
        <h2 className="text-3xl lg:text-4xl font-bold text-ash-800 max-w-xl mx-auto text-center mt-4">
          Here&apos;s What You Get
        </h2>
      </div>

      <div ref={ref} className="relative max-w-7xl mx-auto">
        {whatYouGetSteps.map((item, index) => {
          const isEven = index % 2 === 0;
          return (
            <div key={index} className="w-full mb-4">
              {/* mobile layout - progress bar on left */}
              <div className="lg:hidden relative">
                <div className="flex">
                  {/* adjust dot positioning */}
                  <div className="w-16 relative flex flex-col items-center justify-center z-40">
                    <div className="sticky flex items-center justify-center">
                      <div className="h-8 w-8 rounded-full bg-white flex items-center justify-center">
                        <div className="rounded-full border h-3 w-3 bg-ash-200" />
                      </div>
                    </div>
                  </div>
                  <div className="flex-1 mr-2">{item.content}</div>
                </div>
              </div>

              {/* desktop layout - progress bar in center, image on left, content on right and alternate on each row */}
              <div
                className={cn(
                  "hidden lg:flex justify-between items-center gap-4",
                  isEven ? "flex-row" : "flex-row-reverse"
                )}
              >
                <div className={cn("w-1/2 p-8 flex items-center justify-center", isEven ? "md:pr-12" : "md:pl-12")}>
                  {item.media}
                </div>

                <div className="w-1/2 p-4">{item.content}</div>

                <div className="absolute left-1/2 -translate-x-1/2 z-40">
                  <div className="h-8 w-8 rounded-full bg-white flex shadow-xs border border-ash-200 items-center justify-center">
                    <div className="rounded-full border h-3 w-3 bg-rebolt-500" />
                  </div>
                </div>
              </div>
            </div>
          );
        })}
        {/* progress bar - mobile */}
        <div
          style={{ height: height + "px" }}
          className="lg:hidden absolute md:left-8 left-8 top-0 overflow-hidden w-[2px] bg-[linear-gradient(to_bottom,var(--tw-gradient-stops))] from-transparent from-0% via-ash-200 to-transparent to-99% mask-[linear-gradient(to_bottom,transparent_0%,black_10%,black_90%,transparent_100%)]"
        >
          <motion.div
            style={{
              height: heightTransform,
              opacity: opacityTransform,
            }}
            className="absolute inset-x-0 top-0 w-[2px] bg-linear-to-t from-honey-500 via-rebolt-500 to-transparent from-0% via-10% rounded-full"
          />
        </div>

        {/* progress bar - desktop */}
        <div
          style={{ height: height + "px" }}
          className="hidden lg:block lg:absolute left-1/2 -translate-x-1/2 top-0 bottom-0 overflow-hidden w-[2px] bg-[linear-gradient(to_bottom,var(--tw-gradient-stops))] from-transparent from-0% via-ash-200 to-transparent to-99% mask-[linear-gradient(to_bottom,transparent_0%,black_10%,black_90%,transparent_100%)]"
        >
          <motion.div
            style={{
              height: heightTransform,
              opacity: opacityTransform,
            }}
            className="absolute inset-x-0 top-0 w-[2px] bg-linear-to-t from-honey-500 via-rebolt-500 to-transparent from-0% via-10% rounded-full"
          />
        </div>
      </div>
    </div>
  );
};

function ContentContainer({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex flex-col items-center h-full p-4 md:p-8 justify-between">
      <div className="text-left md:text-center">{children}</div>
    </div>
  );
}

function ContentTitle({ text, className }: { text: string; className?: string }) {
  return <h3 className={cn("text-2xl font-bold text-ash-800 w-full text-left md:text-center", className)}>{text}</h3>;
}

function ContentDescription({ text, className }: { text: string; className?: string }) {
  return <p className={cn("text-ash-600 max-w-md", className)}>{text}</p>;
}

function NumberBadge({ number, tilt, className }: { number: number; tilt: "left" | "right"; className?: string }) {
  return (
    <div className={cn("flex items-center justify-center w-full", className)}>
      <div
        className={cn(
          "w-16 h-16 bg-linear-to-br rounded-2xl flex items-center justify-center from-rebolt-400 to-rebolt-600",
          tilt === "left" ? "-rotate-3" : tilt === "right" ? "rotate-3" : ""
        )}
      >
        <span className="text-3xl text-white font-bold">{number}</span>
      </div>
    </div>
  );
}

export default WhatYouGetSection;
